import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CalendarSyncMenu,
  getCalendarSyncIcon,
} from 'shared/components/ui/calendar-sync-menu';
import { Colors } from 'shared/components/ui/checkbox';
import { Icon } from 'shared/components/ui/icon';
import { Tooltip } from 'shared/components/ui/tooltip';
import { useOpenMenu } from 'shared/hooks/use-open-menu';

import * as Styled from './calendar-event-sync-badge.style';

export type CalendarEventSyncBadgeProps = {
  isSynced: boolean;
  isHidden: boolean;
  onToggleSync: () => void;
  onToggleVisibility: () => void;
  // The card's checkbox color, so the badge matches its border. A task with no color and
  // no priority is the exception — the badge stays grey rather than going primary.main.
  color?: Colors | string;
};

export const CalendarEventSyncBadge: React.FC<CalendarEventSyncBadgeProps> = ({
  isSynced,
  isHidden,
  onToggleSync,
  onToggleVisibility,
  color,
}) => {
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  // Nothing else in the menu applies to an unsynced task — hiding details only
  // means something once it syncs — so the badge syncs on the spot.
  const onBadgeClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!isSynced) {
      onToggleSync();
      return;
    }

    openMenu();
  };

  return (
    <>
      <Styled.Badge
        ref={buttonRef}
        type="button"
        onClick={onBadgeClick}
        aria-label={t(
          isSynced
            ? 'calendar.event-sync.aria-label'
            : 'calendar.event-sync.start',
        )}
        $synced={isSynced}
        $color={color}
      >
        <Icon
          icon={getCalendarSyncIcon(isSynced, isHidden)}
          size={12}
          strokeWidth={2}
        />
      </Styled.Badge>

      {!isSynced && (
        <Tooltip
          referenceElement={buttonRef}
          label={t('calendar.event-sync.start')}
        />
      )}

      {menuOpen && (
        <CalendarSyncMenu
          isSynced={isSynced}
          isHidden={isHidden}
          onToggleSync={onToggleSync}
          onToggleVisibility={onToggleVisibility}
          onClose={closeMenu}
          referenceElement={buttonRef}
        />
      )}
    </>
  );
};
